import { useCallback, useMemo } from 'react';
import { addDayOffset, minDate, thisMonthDate, thisSundayDate, todayDate } from '../../../common/src/date_string.ts';
import {
  ClientOrg,
  Org,
  OrgBase,
  OrgId,
  OrgType,
  schoolOrgTypes,
  SongListType,
  SongSlug,
} from '../../../common/src/model.ts';
import { getSchoolTermDateRange } from '../../../common/src/org.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { OneHourMs } from '../../../common/src/sleep.ts';

import { useOnline } from '../util/use_online.tsx';
import { localStorageGet, LocalStorageKey, localStorageRemove, localStorageSet } from './client_local_storage.ts';
import { addToSongListIdMap, mapSongListId } from './song_list_id_map.tsx';
import {
  classifySongListSyncFailure,
  SongListSaveRefusal,
  SongListSyncFailure,
  SongListSyncRetry,
} from './song_list_sync_retry.ts';
import { getWeeklySongLists } from './song_lists.ts';
import {
  clearAllSongListUpdates,
  DeletableSongList,
  getCurrentSongListUpdateTimestamp,
  getSongListUpdatesBefore,
  removeSongListUpdatesBefore,
  songListUpdatesPending,
} from './song_updates.ts';
import { synchronizeHouseholdWithServer, useHousehold } from './use_household.tsx';
import { useLocalStorage } from './use_local_storage.tsx';
import { getUser } from './use_user.tsx';

export type { SongListSaveRefusal } from './song_list_sync_retry.ts';

export const testOrgIds = [5, 243];
export const songFutureSizeInWeeks = 8;

export function getCurrentOrg() {
  const orgs = localStorageGet(LocalStorageKey.CachedOrgs);
  const currentOrgId = localStorageGet(LocalStorageKey.CurrentOrgId);
  const household = localStorageGet(LocalStorageKey.Household);
  return getCurrentOrgFromOrgs(orgs, household?.orgIds ?? [], currentOrgId);
}

export function useCurrentOrg(): {
  currentOrg: ClientOrg | undefined;
  setCurrentOrg: (org: OrgBase | undefined) => void;
  householdOrgs: ClientOrg[];
  setHouseholdOrg: (org: OrgBase) => Promise<void>;
  clearHouseholdOrgTypes: (orgTypes: OrgType[]) => Promise<void>;
  clearAllHouseholdOrgs: () => Promise<void>;
  // TODO(hewitt): this function should take a callback that accepts the current org & returns a partial
  updateOrgInfo: (org: { id: OrgId } & Partial<Org>) => Promise<void>;
} {
  const { household, setHousehold } = useHousehold();
  const [cachedOrgs, setCachedOrgs] = useLocalStorage(LocalStorageKey.CachedOrgs);
  const [currentOrgId, setCurrentOrgId] = useLocalStorage(LocalStorageKey.CurrentOrgId);
  const isOnline = useOnline();

  const currentOrg = useMemo(
    () => getCurrentOrgFromOrgs(cachedOrgs, household?.orgIds ?? [], currentOrgId),
    [cachedOrgs, currentOrgId, household],
  );

  const setHouseholdOrgIds = useCallback(
    async (orgIds: OrgId[]) => {
      if (!isOnline) {
        throw new Error('An Internet connection is required.');
      }
      // pending org updates are lost when switching orgs
      clearOrgUpdates();
      await clearAllSongListUpdates();
      await setHousehold((value) => {
        if (!value) {
          console.error('ERROR: Household should have been set before setting household org');
          return;
        }
        return { ...value, orgIds };
      });
      await synchronizeOrgsWithServer({ force: true }); // triggers household sync
    },
    [isOnline, setHousehold],
  );

  const setCurrentOrg = useCallback(
    (org: OrgBase | undefined) => {
      if (org) {
        setCachedOrgs((orgs) => [...orgs.filter(({ id }) => id !== org.id), org]);
      }
      setCurrentOrgId(org?.id);
      clearOrgSongLists();
    },
    [setCurrentOrgId, setCachedOrgs],
  );

  const setHouseholdOrg = useCallback(
    async (org: OrgBase) => {
      const otherOrgIds =
        household?.orgIds.filter((id) => {
          if (id === org.id) {
            return false;
          }
          const otherOrg = cachedOrgs.find((otherOrg) => otherOrg.id === id);
          if (!otherOrg) {
            return false;
          }
          const { type } = otherOrg;
          return schoolOrgTypes.includes(org.type) ? !schoolOrgTypes.includes(type) : schoolOrgTypes.includes(type);
        }) ?? [];
      setCurrentOrg(org);
      await setHouseholdOrgIds([...otherOrgIds, org.id]);
    },
    [cachedOrgs, household, setCurrentOrg, setHouseholdOrgIds],
  );

  const clearHouseholdOrgTypes = useCallback(
    async (orgTypes: OrgType[]) => {
      const remainingOrgIds =
        household?.orgIds.filter((id) => {
          const otherOrg = cachedOrgs.find((otherOrg) => otherOrg.id === id);
          return otherOrg && !orgTypes.includes(otherOrg.type);
        }) ?? [];
      if (currentOrgId && !remainingOrgIds.includes(currentOrgId)) {
        setCurrentOrgId(undefined);
        clearOrgSongLists();
      }
      await setHouseholdOrgIds(remainingOrgIds);
    },
    [cachedOrgs, currentOrgId, household?.orgIds, setCurrentOrgId, setHouseholdOrgIds],
  );

  const clearAllHouseholdOrgs = useCallback(async () => {
    await setHouseholdOrgIds([]);
    setCurrentOrgId(undefined);
    clearOrgSongLists();
  }, [setCurrentOrgId, setHouseholdOrgIds]);

  const updateOrgInfo = useCallback(
    async (updates: { id: OrgId } & Partial<Org>) => {
      if (!currentOrg) {
        console.error('ERROR: Attempt to update non-existent organization');
        return;
      }
      const previousUpdates = currentOrg.updates ?? {};
      if (JSON.stringify(previousUpdates) === JSON.stringify({ ...previousUpdates, ...updates })) {
        return;
      }
      setCachedOrgs((prevOrgs) => {
        const otherOrgs = prevOrgs.filter(({ id }) => id != updates.id);
        return [...otherOrgs, { ...currentOrg, ...updates, updates: { ...previousUpdates, ...updates } }];
      });
      await synchronizeOrgsWithServer();
    },
    [currentOrg, setCachedOrgs],
  );

  const householdOrgs = cachedOrgs.filter(({ id }) => household?.orgIds && household.orgIds.includes(id));

  return {
    currentOrg,
    setCurrentOrg,
    householdOrgs,
    setHouseholdOrg,
    clearAllHouseholdOrgs,
    clearHouseholdOrgTypes,
    updateOrgInfo,
  };
}

export async function refreshSingingPlans(forOrgId: OrgId) {
  try {
    await server_api.generateOrgSingingPlans({ forDate: todayDate(), forOrgId });
    await server_api.generateOrgSingingPlans({ forDate: addDayOffset(todayDate(), 1), forOrgId });
  } catch (e: any) {
    console.error(`Failed to refresh singing plans ${e.message}`);
  }
}

export function clearOrgUpdates() {
  const cachedOrgs = localStorageGet(LocalStorageKey.CachedOrgs);
  const clearedOrgs = cachedOrgs.map((org) => {
    const { updates: _, ...orgNoChanges } = org;
    return orgNoChanges;
  });
  if (JSON.stringify(clearedOrgs) === JSON.stringify(cachedOrgs)) {
    return;
  }
  localStorageSet(LocalStorageKey.CachedOrgs, clearedOrgs);
}

// syncs once per day unless forced, pushing, or the org is missing
export async function synchronizeOrgsWithServer({ force }: { force?: boolean } = {}) {
  // ignore network errors
  try {
    // if there are pending household changes, sync them first in case it affects the currently selected church
    const pushHousehold = localStorageGet(LocalStorageKey.PushHousehold);
    if (pushHousehold) {
      await synchronizeHouseholdWithServer({ force });
    }

    // remove org along with household
    const household = localStorageGet(LocalStorageKey.Household);
    if (!household?.token) {
      localStorageRemove(LocalStorageKey.CachedOrgs);
      await clearAllSongListUpdates();
      return;
    }

    const orgs = localStorageGet(LocalStorageKey.CachedOrgs);
    let orgContainsChanges = false;
    try {
      for (const org of orgs) {
        if (org.updates) {
          await server_api.upsertOrg(org.updates);
          orgContainsChanges = true;
        }
      }
    } catch (error: any) {
      // if not authorized, just ignore to prevent the app from getting stuck on a server permission error
      if (!error.message.includes('403')) {
        throw error;
      }
    }
    clearOrgUpdates();

    const mostRecentSyncTimestamp = localStorageGet(LocalStorageKey.MostRecentOrgSyncTimestamp);
    if (
      !force &&
      !pushHousehold &&
      !orgContainsChanges &&
      mostRecentSyncTimestamp &&
      Date.now() - mostRecentSyncTimestamp < OneHourMs
    ) {
      return;
    }

    const serverOrgs = await server_api.getHouseholdOrgs(household.token);
    if (serializeOrgs(orgs) !== serializeOrgs(serverOrgs)) {
      // update/append new server org info (do not remove in case we switch orgs more than once on a slow network)
      localStorageSet(LocalStorageKey.CachedOrgs, [
        ...orgs.filter(({ id }) => !serverOrgs.map(({ id }) => id).includes(id)),
        ...serverOrgs,
      ]);
    }
    localStorageSet(LocalStorageKey.MostRecentOrgSyncTimestamp, Date.now());
  } catch (e: any) {
    console.error(`Failed to sync church with server: ${e.message}`);
  }
}

function serializeOrgs(org: ClientOrg[]): string {
  // avoid taking into account the sync timestamp and change flag when comparing org contents
  return JSON.stringify(
    org.map(({ mostRecentSyncTimestamp: _unused1, updates: _unused2, ...sanitizedOrg }) => sanitizedOrg),
  );
}

export async function synchronizeWeeklySongLists() {
  try {
    await synchronizeOrgsWithServer();
    const currentOrg = getCurrentOrg();

    if (!currentOrg) {
      localStorageRemove(LocalStorageKey.SchoolTerms);
      localStorageRemove(LocalStorageKey.WeeklySongList);
      return;
    }

    const previousSongsForWeek = localStorageGet(LocalStorageKey.WeeklySongList);

    await synchronizeSchoolTerms(currentOrg);
    const songLists = await getSongLists(currentOrg);
    const weeklySongLists = getWeeklySongLists(currentOrg, songLists);

    // only update storage if something changed
    if (JSON.stringify(weeklySongLists) !== JSON.stringify(previousSongsForWeek)) {
      localStorageSet(LocalStorageKey.WeeklySongList, weeklySongLists);
    }
  } catch (e: any) {
    console.error(`Failed to sync weekly song lists: ${e.message}`);
  }
}

export async function synchronizeSchoolTerms(school?: OrgBase) {
  try {
    if (!school || school.type !== OrgType.School) {
      localStorageRemove(LocalStorageKey.SchoolTerms);
      return;
    }
    const previousSchoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
    const schoolTerms = await server_api.getCustomSongIntervals(school.id);
    if (JSON.stringify(schoolTerms) !== JSON.stringify(previousSchoolTerms)) {
      localStorageSet(LocalStorageKey.SchoolTerms, schoolTerms);
    }
  } catch (e: any) {
    console.error(`Failed to sync school terms: ${e.message}`);
  }
}

// note that school terms must be synchronized first if requesting for a school
export async function getSongLists(church: OrgBase) {
  let fromDate = todayDate();
  if (church.type === OrgType.School) {
    const schoolTerms = localStorageGet(LocalStorageKey.SchoolTerms);
    if (schoolTerms.length !== 0) {
      const termDateRange = getSchoolTermDateRange(schoolTerms, todayDate());
      if (termDateRange) {
        fromDate = termDateRange.start;
      }
    }
  } else {
    fromDate = thisMonthDate(thisSundayDate(todayDate()));
  }
  // be sure to go at least one week back to include events
  return await server_api.getSongLists({
    churchId: church.id,
    from: minDate([fromDate, addDayOffset(todayDate(), -7)]),
  });
}

// The sync pass or discard in progress: only one runs at a time (issue #3609). Cleared by
// finishSongListSyncWork in the same synchronous step that reads synchronizeRequested, so no request is lost.
let songListSyncWork: Promise<void> | undefined;
// a sync was requested while songListSyncWork was running: run another pass as soon as it finishes
let synchronizeRequested = false;
const songListSyncRetry = new SongListSyncRetry();

/**
 * Uploads the pending song list updates, then downloads the server's lists. Called after every local edit
 * and when a page that shows song lists opens: it starts the refusal backoff over and tries now.
 */
export async function synchronizeAllSongLists() {
  songListSyncRetry.resetBackoff();
  await runSongListSync();
}

/** The admin asked to try saving again: start the backoff over and try now. */
export async function retrySongListSync() {
  await synchronizeAllSongLists();
}

/** Set while the server refuses the content of the pending song list updates (issue #3609). */
export function getSongListSaveRefusal(): SongListSaveRefusal | undefined {
  return songListSyncRetry.refusal;
}

/**
 * Throws the pending song list updates away and takes the server's lists for the current church, e.g.
 * after the server has refused them. Waits for a sync pass in progress to finish first, so an upload
 * running now cannot schedule a retry or set a refusal after the discard. Rejects if the download fails
 * (the updates are gone either way; the next sync downloads the lists).
 */
export async function discardPendingSongListUpdates() {
  while (songListSyncWork) {
    await songListSyncWork.catch(() => {});
  }
  songListSyncWork = discardPass();
  await songListSyncWork;
}

async function discardPass() {
  synchronizeRequested = false;
  songListSyncRetry.cancelRetry();
  songListSyncRetry.recordSuccess();
  try {
    await clearAllSongListUpdates();
    const currentOrg = getCurrentOrg();
    if (currentOrg) {
      await downloadServerSongLists(currentOrg);
    }
  } finally {
    await finishSongListSyncWork(0);
  }
}

async function runSongListSync() {
  if (songListSyncWork) {
    // e.g. an edit made while a sync is uploading: sync again as soon as this one finishes
    synchronizeRequested = true;
    return;
  }
  songListSyncWork = songListSyncPass();
  await songListSyncWork;
}

async function songListSyncPass() {
  synchronizeRequested = false;
  songListSyncRetry.cancelRetry();
  let retryDelayMs = 0;
  // only the upload can be refused: a 4xx from the org sync, the download or school terms is transient
  let uploading = false;
  try {
    await synchronizeOrgsWithServer();
    const currentOrg = getCurrentOrg();
    const household = localStorageGet(LocalStorageKey.Household);
    if (!currentOrg || !household) {
      localStorageRemove(LocalStorageKey.AllSongLists);
      localStorageRemove(LocalStorageKey.SchoolTerms);
      await clearAllSongListUpdates();
      songListSyncRetry.recordSuccess();
      return;
    }
    uploading = true;
    await uploadUpdatedSongLists(currentOrg);
    uploading = false;
    songListSyncRetry.recordSuccess();
    if (!(await songListUpdatesPending())) {
      await downloadServerSongLists(currentOrg, songListUpdatesPending);
    }
    await synchronizeSchoolTerms(currentOrg);
  } catch (error: unknown) {
    retryDelayMs = songListSyncRetry.recordFailure(await classifyFailure(error, uploading));
  } finally {
    await finishSongListSyncWork(retryDelayMs);
  }
}

/** Ends every sync pass and discard: frees the slot, schedules the next pass if needed, notifies listeners. */
async function finishSongListSyncWork(retryDelayMs: number) {
  try {
    const pending = await songListUpdatesPending();
    songListSyncWork = undefined;
    if (pending || synchronizeRequested) {
      songListSyncRetry.scheduleRetry(synchronizeRequested ? 0 : retryDelayMs, () => void runSongListSync());
    }
  } finally {
    songListSyncWork = undefined;
    invokeSongListSyncListeners();
  }
}

async function classifyFailure(error: unknown, duringUpload: boolean): Promise<SongListSyncFailure> {
  const failure = classifySongListSyncFailure(error);
  if (failure.kind === 'forbidden') {
    // user is not authorized - prevent the app from getting stuck on a server permission error
    try {
      await clearAllSongListUpdates();
      const currentOrg = getCurrentOrg();
      if (currentOrg) {
        await downloadServerSongLists(currentOrg);
      }
    } catch (e: unknown) {
      console.error(`Failed to download song lists after a 403: ${(e as Error)?.message}`);
    }
    return failure;
  }
  if (failure.kind === 'refused' && !duringUpload) {
    // a later request (org sync, download, school terms) failed: the server refused no song list update
    return { kind: 'transient' };
  }
  return failure;
}

const songListSyncListeners: Array<() => void> = [];

export function addSongListSyncListener(listener: () => void) {
  songListSyncListeners.push(listener);
}

export function removeSongListSyncListener(listener: () => void) {
  const index = songListSyncListeners.indexOf(listener);
  if (index !== -1) {
    songListSyncListeners.splice(index, 1);
  }
}

function invokeSongListSyncListeners() {
  for (const listener of songListSyncListeners) {
    listener();
  }
}

async function uploadUpdatedSongLists(org: ClientOrg) {
  const currentTimestamp = getCurrentSongListUpdateTimestamp();
  const songLists = await getSongListUpdatesBefore(currentTimestamp);
  const updatedLists = songLists.filter((songList) => !songList.isDeleted);
  const deletedLists = songLists.filter((songList) => songList?.isDeleted && songList.id && songList.id >= 0);
  if (updatedLists.length > 0) {
    const newIds = await server_api.upsertSongLists(org.id, removeNegativeIds(updatedLists));
    for (const [index, songList] of updatedLists.entries()) {
      if (songList.id && songList.id < 0) {
        addToSongListIdMap(songList.id, newIds[index]!);
      }
    }
  }
  if (deletedLists.length > 0) {
    await server_api.deleteSongLists(
      org.id,
      deletedLists.map(({ id }) => mapSongListId(id!)),
    );
  }
  await removeSongListUpdatesBefore(currentTimestamp);
}

function removeNegativeIds(songLists: DeletableSongList[]): DeletableSongList[] {
  return songLists.map((songList) => (songList.id && songList.id >= 0 ? songList : { ...songList, id: undefined }));
}

async function downloadServerSongLists(church: ClientOrg, cancel?: () => Promise<boolean>) {
  const allSongs = await server_api.getSongLists({ churchId: church.id });
  const isInternalUser = !!getUser()?.internal;
  const seasonalSongLists = isInternalUser ? await server_api.getSeasonalSongLists() : [];

  // Toss the results at the last minute if song list updates are pending because otherwise the pending changes
  // disappear from LocalStorageKey.AllSongLists and therefore from the liturgy planner.
  // We will eventually update our local storage to match the server, but only after all the updates have
  // been updated.
  if (!cancel || !(await cancel())) {
    localStorageSet(LocalStorageKey.AllSongLists, [...allSongs, ...seasonalSongLists]);
  }
}

export function setSongTempoForOrg({
  org,
  songSlug,
  songTempo,
}: {
  org: ClientOrg;
  songSlug: SongSlug;
  songTempo: number;
}): Pick<ClientOrg, 'id' | 'songTempos'> {
  const songTempos = { ...org.songTempos, [songSlug]: songTempo };
  return { id: org.id, songTempos };
}

export function setSongTranspositionForOrg({
  org,
  songSlug,
  modulationHalfSteps,
}: {
  org: ClientOrg;
  songSlug: SongSlug;
  modulationHalfSteps: number;
}): { id: OrgId } & Pick<Org, 'songTranspositions'> {
  const songTranspositions = { ...org.songTranspositions };
  delete songTranspositions[songSlug];
  if (modulationHalfSteps !== 0) {
    songTranspositions[songSlug] = modulationHalfSteps;
  }
  return { id: org.id, songTranspositions };
}

function getCurrentOrgFromOrgs(
  orgs: ClientOrg[],
  householdOrgIds: OrgId[],
  currentOrgId: number | undefined,
): ClientOrg | undefined {
  const householdOrgs = householdOrgIds.map((id) => orgs.find(({ id: orgId }) => orgId === id)).filter((org) => !!org);
  if (householdOrgs.length === 0) {
    return undefined;
  }
  return currentOrgId && householdOrgs.find(({ id }) => id === currentOrgId)
    ? householdOrgs.filter(({ id }) => id === currentOrgId)[0]
    : (householdOrgs.filter(({ type }) => type === OrgType.Church)[0] ?? orgs[0]);
}

export function useOrgEventsViewed() {
  const [mostRecentlyViewedOrgEvents, setMostRecentlyViewedOrgEvents] = useLocalStorage(
    LocalStorageKey.MostRecentlyViewedOrgEvents,
  );
  const [weeklySongList] = useLocalStorage(LocalStorageKey.WeeklySongList);
  const currentEvents = weeklySongList?.filter(({ type }) => type === SongListType.Event);
  const eventUpdatesViewed =
    currentEvents?.length === 0 || JSON.stringify(mostRecentlyViewedOrgEvents) === JSON.stringify(currentEvents);
  const setEventUpdatesViewed = useCallback(() => {
    setMostRecentlyViewedOrgEvents(currentEvents);
  }, [currentEvents, setMostRecentlyViewedOrgEvents]);
  return { eventUpdatesViewed, mostRecentlyViewedOrgEvents, setEventUpdatesViewed };
}

function clearOrgSongLists() {
  localStorageRemove(LocalStorageKey.WeeklySongList);
  localStorageRemove(LocalStorageKey.AllSongLists);
}
